import { DOMProps } from '@react-types/shared';
import { filterDOMProps } from '@react-aria/utils';
import React, { ElementType, ForwardedRef, forwardRef } from 'react';
import clsx from 'clsx';
import styles from '@danske/sapphire-css/components/text/text.module.css';
import {
  SapphireStyleProps,
  TextStyleProps,
  useTextStyleProps,
  useThemeCheck,
} from '../../utils';
import { GlobalDomAttributes } from '../../utils/types';

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;
type HeadingSize = '2xl' | 'xl' | 'lg' | 'md' | 'sm' | 'xs';

export interface TypographyHeadingProps
  extends DOMProps,
    TextStyleProps,
    SapphireStyleProps,
    GlobalDomAttributes {
  children: React.ReactNode;
  /**
   * The level of the heading.
   */
  level: HeadingLevel;
  /**
   * The semantic color of the text
   * @default 'primary'
   */
  color?:
    | 'primary'
    | 'secondary'
    | 'positive'
    | 'negative'
    | 'warning'
    | 'informative';
  elementType?: `h${HeadingLevel}` | ElementType;
}

export const Heading = forwardRef(function Heading(
  props: TypographyHeadingProps,
  ref: ForwardedRef<HTMLHeadingElement>
) {
  useThemeCheck();

  const domProps = filterDOMProps(props, { global: true });
  const {
    children,
    level,
    color = 'primary',
    elementType,
    ...otherProps
  } = props;
  const { styleProps } = useTextStyleProps(otherProps);
  const HeadingTag = elementType ? elementType : (`h${level}` as ElementType);
  const colorClassName =
    color === 'primary'
      ? undefined
      : styles[`sapphire-text--${color}` as const];

  return (
    <HeadingTag
      {...domProps}
      {...styleProps}
      className={clsx(
        styles['sapphire-text'],
        styles[`sapphire-text--heading-${levelToSize(level)}`],
        colorClassName,
        styleProps.className
      )}
      ref={ref}
    >
      {children}
    </HeadingTag>
  );
});

const levelToSize = (level: HeadingLevel): HeadingSize =>
  ((
    {
      1: '2xl',
      2: 'xl',
      3: 'lg',
      4: 'md',
      5: 'sm',
      6: 'xs',
    } as const
  )[level]);
